import React from 'react';
import { BarChart3, FileText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const cardShell =
  'bg-white dark:bg-[#1C1C1E] rounded-2xl border border-gray-100 dark:border-white/5 overflow-hidden';

const Th = ({ children, className = '' }) => (
  <th className={`text-left py-2 px-3 font-semibold text-gray-400 ${className}`}>{children}</th>
);

const renderAktifPasifKalem = (item, idx, prefix, formatNumber) => {
  const isGrupToplam =
    item.type === 'kalem' &&
    (item.accountCode === '1' ||
      item.accountCode === '2' ||
      item.accountCode === '3' ||
      item.accountCode === '4' ||
      item.accountCode === '5' ||
      item.accountCode === '6');
  const isAltGrup =
    item.type === 'kalem' &&
    item.accountCode &&
    item.accountCode.length === 2 &&
    (item.accountCode.startsWith('1') ||
      item.accountCode.startsWith('2') ||
      item.accountCode.startsWith('3') ||
      item.accountCode.startsWith('4') ||
      item.accountCode.startsWith('5') ||
      item.accountCode.startsWith('6'));

  if (item.type === 'toplam') {
    return (
      <tr
        key={`${prefix}-toplam-${item.baslik}-${idx}`}
        className={
          item.isBold
            ? 'font-bold border-t-2 border-gray-300 dark:border-white/20'
            : 'font-semibold border-t border-gray-100 dark:border-white/5'
        }
      >
        <td className="py-2 px-3 font-mono text-xs text-gray-600 dark:text-gray-300">{item.kod || ''}</td>
        <td className="py-2 px-3 text-gray-800 dark:text-gray-200">{item.baslik}</td>
        <td className="py-2 px-3 text-right font-mono text-xs text-gray-800 dark:text-gray-200">
          {formatNumber(item.deger || 0)}
        </td>
      </tr>
    );
  }

  if (isGrupToplam || isAltGrup) {
    return (
      <tr key={`${prefix}-${item.accountCode}-${item.index}`} className="font-bold">
        <td className="py-2 px-3 font-mono text-xs text-gray-800 dark:text-gray-200">{item.accountCode || ''}</td>
        <td className="py-2 px-3 text-gray-900 dark:text-white">{item.accountName || ''}</td>
        <td className="py-2 px-3 text-right font-mono text-xs text-gray-900 dark:text-white">
          {formatNumber(item.bilancoDegeri || 0)}
        </td>
      </tr>
    );
  }

  return (
    <tr
      key={`${prefix}-${item.accountCode}-${item.index}`}
      className="text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
    >
      <td className="py-1.5 px-3 pl-5 font-mono text-xs">{item.accountCode || ''}</td>
      <td className="py-1.5 px-3">{item.accountName || ''}</td>
      <td className="py-1.5 px-3 text-right font-mono text-xs">
        {formatNumber(item.bilancoDegeri || 0)}
      </td>
    </tr>
  );
};

const TrialBalanceSheetView = ({
  aktifSonuc,
  pasifSonuc,
  gelirSonuc,
  totals,
  pasifToplamDeger,
  selectedPeriod,
  donemNetKariDegeri,
  hesaplananDonemNetKari,
  donemNetKariDegeri2,
  formatNumber,
}) => {
  const { t } = useLanguage();

  return (
    <div className="py-2 space-y-6">
        {/* Aktif */}
        <div className={cardShell}>
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5 flex items-center gap-2">
            <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
            <h3 className="text-sm font-bold text-blue-600 dark:text-blue-400">{t('edefter.activeSide')}</h3>
          </div>
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100 dark:border-white/5">
                  <Th>{t('edefter.accountCodeCol')}</Th>
                  <Th>{t('edefter.accountTitleCol')}</Th>
                  <Th className="text-right">{t('edefter.amountCol')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                {aktifSonuc.map((item, idx) => renderAktifPasifKalem(item, idx, 'aktif', formatNumber))}
                <tr className="font-bold border-t-2 border-gray-300 dark:border-white/20">
                  <td className="py-3 px-3" />
                  <td className="py-3 px-3 text-blue-600 dark:text-blue-400">{t('edefter.totalAssets')}</td>
                  <td className="py-3 px-3 text-right font-mono text-blue-600 dark:text-blue-400">
                    {formatNumber(totals.aktifToplam)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Pasif */}
        <div className={cardShell}>
          <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5 flex items-center gap-2">
            <BarChart3 size={16} className="text-purple-600 dark:text-purple-400" />
            <h3 className="text-sm font-bold text-purple-600 dark:text-purple-400">{t('edefter.passiveSide')}</h3>
          </div>
          <div className="p-5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400 border-b border-gray-100 dark:border-white/5">
                  <Th>{t('edefter.accountCodeCol')}</Th>
                  <Th>{t('edefter.accountTitleCol')}</Th>
                  <Th className="text-right">{t('edefter.amountCol')}</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-white/5">
                {pasifSonuc.map((item, idx) => renderAktifPasifKalem(item, idx, 'pasif', formatNumber))}
                <tr className="font-bold border-t-2 border-gray-300 dark:border-white/20">
                  <td className="py-3 px-3" />
                  <td className="py-3 px-3 text-purple-600 dark:text-purple-400">{t('edefter.totalLiabilities')}</td>
                  <td className="py-3 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                    {formatNumber(pasifToplamDeger)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      {/* Gelir Tablosu */}
      <div className={cardShell}>
        <div className="px-5 py-4 border-b border-gray-100 dark:border-white/5 flex items-center gap-2">
          <FileText size={16} className="text-[#24299d] dark:text-[#818cf8]" />
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">{t('edefter.incomeStatementTitle')}</h3>
        </div>
        <div className="p-5 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100 dark:border-white/5">
                <Th>{t('edefter.accountCodeCol')}</Th>
                <Th>{t('edefter.accountTitleCol')}</Th>
                <Th className="text-right">{t('edefter.amountCol')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-white/5">
              {gelirSonuc.map((item, idx) => {
                const isGelirGrup = item.type === 'kalem' && item.accountCode === '6';
                const isGelirAltGrup =
                  item.type === 'kalem' &&
                  item.accountCode &&
                  item.accountCode.length === 2 &&
                  item.accountCode.startsWith('6');
                if (isGelirGrup || isGelirAltGrup) {
                  return (
                    <tr key={`gelir-${item.accountCode}-${item.index}`} className="font-bold">
                      <td className="py-2 px-3 font-mono text-xs">{item.accountCode || ''}</td>
                      <td className="py-2 px-3">{item.accountName || ''}</td>
                      <td className="py-2 px-3 text-right font-mono text-xs">
                        {formatNumber(item.bilancoDegeri || 0)}
                      </td>
                    </tr>
                  );
                }
                if (item.type === 'kalem') {
                  return (
                    <tr
                      key={`gelir-${item.accountCode}-${item.index}`}
                      className="text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5"
                    >
                      <td className="py-1.5 px-3 pl-5 font-mono text-xs">{item.accountCode || ''}</td>
                      <td className="py-1.5 px-3">{item.accountName || ''}</td>
                      <td className="py-1.5 px-3 text-right font-mono text-xs">
                        {formatNumber(item.bilancoDegeri || 0)}
                      </td>
                    </tr>
                  );
                }
                return null;
              })}
              {selectedPeriod !== '4' && donemNetKariDegeri >= 0 && (
                <>
                  <tr className="font-semibold border-t border-gray-100 dark:border-white/5">
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3">BRÜT KAR</td>
                    <td className="py-2 px-3 text-right font-mono">{formatNumber(hesaplananDonemNetKari)}</td>
                  </tr>
                  <tr className="font-semibold">
                    <td className="py-2 px-3" />
                    <td className="py-2 px-3">Ödenecek vergiler</td>
                    <td className="py-2 px-3 text-right font-mono">{formatNumber(donemNetKariDegeri2)}</td>
                  </tr>
                </>
              )}
              {selectedPeriod !== '4' && (
                <tr className="font-bold border-t-2 border-gray-300 dark:border-white/20">
                  <td className="py-3 px-3" />
                  <td className="py-3 px-3 text-gray-900 dark:text-white">
                    {donemNetKariDegeri >= 0 ? '590 - DÖNEM NET KARI' : '591 - DÖNEM NET ZARARI'}
                  </td>
                  <td className="py-3 px-3 text-right font-mono text-gray-900 dark:text-white">
                    {formatNumber(donemNetKariDegeri)}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceSheetView;
